import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { setProductData, addProductType, updateProductType } from '../store/productSlice';
import { PW_Products_API, PW_ProductType_Save_API } from '../Server/ApiV2';
import Spinner from './Spinner';

export default function ProductTypes() {
  const dispatch = useDispatch();
  const { productTypes, productTypeLinks } = useSelector(s => s.product);
  const [pageLoading, setPageLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const res = await PW_Products_API();
      if (cancelled) return;
      if (res?.data?.success) dispatch(setProductData(res.data.data));
      setPageLoading(false);
    })();
    return () => { cancelled = true; };
  }, [dispatch]);
  const [showAdd, setShowAdd] = useState(false);
  const [newType, setNewType] = useState({ name: '', label: '', allowedDenominations: [] });
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({ label: '', allowedDenominations: [], status: 'Active' });

  const handleToggleDenom = (denom, current) => {
    if (current.includes(denom)) return current.filter(d => d !== denom);
    return [...current, denom].sort((a, b) => a - b);
  };

  const handleAdd = () => {
    if (!newType.name.trim()) return;
    const typeData = {
      id: newType.name.toUpperCase().replace(/\s/g, ''),
      name: newType.name,
      label: newType.label || newType.name,
      allowedDenominations: newType.allowedDenominations,
      status: 'Active',
    };
    dispatch(addProductType(typeData));
    PW_ProductType_Save_API(typeData).catch(err => console.error('[ProductTypes] Save failed:', err));
    setNewType({ name: '', label: '', allowedDenominations: [] });
    setShowAdd(false);
  };

  const getLinkedProducts = (typeId) => {
    return productTypeLinks.filter(l => l.typeId === typeId && l.linked).length;
  };

  if (pageLoading) return <div className="page-loading"><Spinner /></div>;

  return (
    <div>
      <div className="page-header">
        <div>
          <h2>Product Types</h2>
          <div className="page-header-sub">Master list of denomination sets — link to products on the Products page</div>
        </div>
        <button className="btn btn-gold" onClick={() => setShowAdd(true)}>+ Add Type</button>
      </div>

      {showAdd && (
        <div className="card mb-24">
          <div className="card-title gold">New Product Type</div>
          <div className="card-body">
            <div className="form-row mb-16">
              <div className="form-group">
                <label className="form-label">Type Name</label>
                <input className="form-input" placeholder="e.g. N4"
                  value={newType.name} onChange={e => setNewType({ ...newType, name: e.target.value })} />
              </div>
              <div className="form-group">
                <label className="form-label">Label</label>
                <input className="form-input" placeholder="e.g. Normal 4"
                  value={newType.label} onChange={e => setNewType({ ...newType, label: e.target.value })} />
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">Allowed Denominations</label>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {[5, 10, 20, 30, 50, 70, 100, 200, 500, 1000, 5000].map(d => (
                  <label key={d} style={{ display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer',
                    padding: '6px 12px', borderRadius: 8,
                    background: newType.allowedDenominations.includes(d) ? '#ede8f5' : '#f5f3fa',
                    border: `1.5px solid ${newType.allowedDenominations.includes(d) ? '#4a3594' : '#e0dce8'}`,
                    fontWeight: 600, fontSize: 13,
                  }}>
                    <input type="checkbox" style={{ accentColor: '#2d1b69' }}
                      checked={newType.allowedDenominations.includes(d)}
                      onChange={() => setNewType({
                        ...newType,
                        allowedDenominations: handleToggleDenom(d, newType.allowedDenominations),
                      })} />
                    {d.toLocaleString()}
                  </label>
                ))}
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
              <button className="btn btn-success" onClick={handleAdd}
                disabled={!newType.name.trim() || newType.allowedDenominations.length < 3}>
                Save Type
              </button>
              <button className="btn btn-secondary" onClick={() => setShowAdd(false)}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      <div className="card">
        <div className="card-title">
          All Product Types
          <span style={{ marginLeft: 'auto', fontSize: 12, color: '#6b6b80' }}>{productTypes.length} types</span>
        </div>
        <div className="card-body" style={{ padding: 0 }}>
          <div className="table-wrap">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Type</th>
                  <th>Label</th>
                  <th>Allowed Denominations</th>
                  <th>Linked</th>
                  <th>Status</th>
                  <th style={{ width: 120 }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {productTypes.length === 0 ? (
                  <tr><td colSpan={6} className="text-center text-muted" style={{ padding: 32 }}>No types configured</td></tr>
                ) : [...productTypes].sort((a, b) => a.id.localeCompare(b.id, undefined, { numeric: true })).map(t => {
                  const isEditing = editingId === t.id;
                  const linkedCount = getLinkedProducts(t.id);
                  if (isEditing) {
                    return (
                      <tr key={t.id} style={{ background: '#f9f8fc' }}>
                        <td data-label="Type"><span className="fw-700">{t.name}</span></td>
                        <td data-label="Label">
                          <input className="form-input" style={{ width: 120 }}
                            value={editForm.label}
                            onChange={e => setEditForm({ ...editForm, label: e.target.value })} />
                        </td>
                        <td data-label="Allowed Denominations">
                          <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                            {[5, 10, 20, 30, 50, 70, 100, 200, 500, 1000, 5000].map(d => (
                              <label key={d} style={{ display: 'flex', alignItems: 'center', gap: 3, cursor: 'pointer',
                                padding: '3px 8px', borderRadius: 6, fontSize: 12, fontWeight: 600,
                                background: editForm.allowedDenominations.includes(d) ? '#ede8f5' : '#f5f3fa',
                                border: `1.5px solid ${editForm.allowedDenominations.includes(d) ? '#4a3594' : '#e0dce8'}`,
                              }}>
                                <input type="checkbox" style={{ accentColor: '#2d1b69', width: 13, height: 13 }}
                                  checked={editForm.allowedDenominations.includes(d)}
                                  onChange={() => setEditForm({
                                    ...editForm,
                                    allowedDenominations: handleToggleDenom(d, editForm.allowedDenominations),
                                  })} />
                                {d.toLocaleString()}
                              </label>
                            ))}
                          </div>
                        </td>
                        <td data-label="Linked" className="fw-600">{linkedCount} products</td>
                        <td data-label="Status">
                          <select className="form-select" style={{ width: 100 }}
                            value={editForm.status}
                            onChange={e => setEditForm({ ...editForm, status: e.target.value })}>
                            <option value="Active">Active</option>
                            <option value="Inactive">Inactive</option>
                          </select>
                        </td>
                        <td data-label="Actions">
                          <div style={{ display: 'flex', gap: 6 }}>
                            <button className="btn btn-success btn-sm"
                              disabled={editForm.allowedDenominations.length < 3}
                              onClick={() => {
                                const updateData = {
                                  id: t.id, name: t.name,
                                  label: editForm.label,
                                  allowedDenominations: editForm.allowedDenominations,
                                  status: editForm.status,
                                };
                                dispatch(updateProductType(updateData));
                                PW_ProductType_Save_API(updateData).catch(err => console.error('[ProductTypes] Update failed:', err));
                                setEditingId(null);
                              }}>Save</button>
                            <button className="btn btn-secondary btn-sm"
                              onClick={() => setEditingId(null)}>Cancel</button>
                          </div>
                        </td>
                      </tr>
                    );
                  }
                  return (
                    <tr key={t.id}>
                      <td data-label="Type"><span className="fw-700">{t.name}</span></td>
                      <td data-label="Label">{t.label}</td>
                      <td data-label="Allowed Denominations">
                        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                          {(t.allowedDenominations || []).map(d => (
                            <span key={d} className="chip chip-gold">{d.toLocaleString()}</span>
                          ))}
                        </div>
                      </td>
                      <td data-label="Linked">
                        <span className={`chip ${linkedCount > 0 ? 'chip-green' : 'chip-red'}`}>
                          {linkedCount} products
                        </span>
                      </td>
                      <td data-label="Status">
                        <span className={t.status === 'Active' ? 'status-active' : 'status-inactive'}>
                          {t.status}
                        </span>
                      </td>
                      <td data-label="Actions">
                        <button className="btn btn-outline btn-sm"
                          onClick={() => {
                            setEditingId(t.id);
                            setEditForm({ label: t.label, allowedDenominations: [...(t.allowedDenominations || [])], status: t.status });
                          }}>Edit</button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
